import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import { motion, AnimatePresence } from "framer-motion";

const ROLE_OPTIONS = [
  "STUDENT", "PARENT", "MENTOR", "INSTITUTION", "DIRECTOR", "COORDINATOR", "INFLUENCER", "FOUNDHER", "MARKETER", "MERCHANT"
];

const PDOnboarding = ({ t, selectedLanguage, role, isNew }) => {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();

  const userId = authState?.user?._id || null;
  const userCode = authState?.user?.think5whyscode || "";
  const userName = authState?.user?.name || "";
  const token = authState?.token;
  const email = authState?.user?.email || "";
  const mobile = authState?.user?.mobile || "";
  const [showFees, setShowFees] = useState(false);
  const emptyForm = {
    userCode,
    userName,
    referredByCode: "",
    referredByName: "",
    preferredActiveRole: role,
    mobileNumber: mobile,
    emailId: email,
    remarks: "",
  };

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState("");
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [justOnboarded, setJustOnboarded] = useState(false);
  const isReadOnly = false;  

  const [showReferralHelp, setShowReferralHelp] = useState(false);
  const [showRoleHelp, setShowRoleHelp] = useState(false);

  const fetchMyOnboarding = async () => {
    try {
      setLoading(true);

      const res = await fetchData({
        url: "/pd/onboarding/my",
        method: "post",
        data: { userId },
        headers: { Authorization: token },
      });

      if (res?.status && res.record) {
        setForm({
          ...res.record,
          preferredActiveRole: res.record?.preferredActiveRole || "STUDENT",
        });
      } else {
        setForm(emptyForm);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchMyOnboarding();
  }, [userId, isNew]);

  const selectRole = (role) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      preferredActiveRole: role,
    }));
  };

  const handleCreate = async () => {
    if (isReadOnly) return;
    if (!form.referredByCode?.trim() || form.referredByCode.length !== 14)
      return setMsg("❌ Valid 14 character Referred By Code required");

    if (!form.referredByName?.trim())
      return setMsg("❌ Referred By Name required");

    if (!form.mobileNumber?.trim() || form.mobileNumber.length !== 10)
      return setMsg("❌ Valid 10 digit mobile number required");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailId || ""))
      return setMsg("❌ Valid email required");

    try {
      setLoading(true);
      setMsg("");

      const payload = {
        ...form,
        preferredActiveRole: form.preferredActiveRole || "STUDENT",
        roles: form.preferredActiveRole ? [form.preferredActiveRole] : [],
      };

      const res = await fetchData({
        url: "/pd/onboarding/create",
        method: "post",
        data: payload,
        headers: { Authorization: token },
      });

      if (res?.status) {
        setIsOnboarded(true);
        setMsg(
          `✅ Successfully Onboarded as ${form.preferredActiveRole}. Refresh Screen to update your complete profile.`
        );
        setTimeout(() => {
          setJustOnboarded(true);
          setMsg("");
        }, 3000);
      }
    } catch (err) {
      setMsg("❌ Server error while creating");
    } finally {
      setLoading(false);
    }
  };

  // const [acquiringRole, setAcquiringRole] = useState(role);
  const [acquiringRole, setAcquiringRole] = useState(false);
  const [acquiredRoles, setAcquiredRoles] = useState([]);

  const handleAcquireRole = async () => {
    try {
      setAcquiringRole(true);
      setMsg("");

      const res = await fetchData({
        url: "/pd/onboarding/acquire-role",
        method: "put",
        data: {
          role: role?.toUpperCase()?.trim(),
        },
        headers: {
          Authorization: token,
        },
      });

      if (res?.status) {
        setMsg(`✅ You have acquired ${role} Role in PurpleDoor by Think5Whys!`);
        setForm((prev) => ({
          ...prev,
          roles: [...(prev?.roles || []), String(role).toUpperCase()],
        }));
      } else {
        setMsg(res?.msg || "❌ Failed to acquire role");
      }
    } catch (err) {
      setMsg("❌ Server error while acquiring role");
    } finally {
      setAcquiringRole(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-900 p-5 rounded-xl text-white">
        Loading onboarding...
      </div>
    );
  }

  const feeFields = [
    { key: "percentServiceFee", label: "Service Fee % of Product Price", value: form?.percentServiceFee ?? 0.4 },
    { key: "percentDirector", label: "Director Share %" },
    { key: "percentCoordinator", label: "Coordinator Share %" },
    { key: "percentInfluencer", label: "Influencer Share %" },
    { key: "percentOthers", label: "Other Allocation %" },
  ];

  const handleAcquireRoleClick = () => {
    // STEP 1: First confirmation
    const firstConfirm = window.confirm(
      `⚠️ You are about to acquire the ${role} role.\n\nDo you want to continue?`
    );
    if (!firstConfirm) return;
    // STEP 2: Second confirmation (final safety layer)
    const secondConfirm = window.confirm(
      `🚨 FINAL CONFIRMATION\n\nPlease confirm again:\nAcquire ${role} role?`
    );
    if (!secondConfirm) return;
    handleAcquireRole();
  };

  const hasRoleAlready = authState?.user?.modules
    ?.find((m) => m.moduleCode === "PD")
    ?.roleCodes?.some(
      (r) => r.roleCode === String(role).toUpperCase()
    );

  return (
    <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
{/* <div className="text-xs text-gray-300 bg-gray-900 p-3 rounded">
  <pre>{JSON.stringify(authState, null, 2)}</pre>
</div>     */}
{/* <div className="text-xs text-gray-300 bg-gray-900 p-3 rounded">
  <pre>{JSON.stringify(emptyForm, null, 2)}</pre>
</div>        */}
{/* {isNew? "New":"Not New}"} */}
      {/* STATUS */}
      {/* {msg && (
        <div className="text-sm text-green-400">{msg}</div>
      )} */}

      {!isNew && (
        <div className="text-xs text-center text-yellow-400 bg-gray-800 p-2 rounded">
          Onboarding already completed!! Refresh this Screen and Update Other Sections in PDProfile as {
            (() => {
              const r = role || form?.preferredActiveRole || "";
              return r ? r.charAt(0).toUpperCase() + r.slice(1).toLowerCase() : "";
            })()
          }
        </div>
      )}
      { (form.preferredActiveRole === "DIRECTOR" || form.preferredActiveRole === "COORDINATOR")  && (
        <div className="mt-2 border-t border-yellow-100 py-2">
          <div
            onClick={() => setShowFees(!showFees)}
            className="flex items-center justify-between cursor-pointer select-none"
          >
            <h2 className="text-yellow-200 text-sm lg:text-lg font-semibold">
              ⚡ Team Suggesred Fee Allocation
            </h2>

            <motion.span
              animate={{ rotate: showFees ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="text-yellow-200 text-lg"
            >
              ⌄
            </motion.span>
          </div>

          <AnimatePresence>
            {showFees && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className="overflow-hidden mt-4"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {feeFields.map((field) => (
                    <div
                      key={field.key}
                      className="bg-gray-800 p-3 rounded-md border border-gray-700 hover:border-yellow-400 transition-all duration-200"
                    >
                      <p className="text-xs lg:text-sm text-gray-200">
                        {field.label}
                      </p>

                      <p className="text-white font-semibold text-sm lg:text-base">
                        {isNaN(Number(field.value))
                        ? "0.00"
                        : Number(field.value).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      {!isNew  && (
        <div className="flex flex-col items-center text-white gap-2">
          {/* {"Role coming as param: "}{role} */}
          {/* {msg && ({msg})} */}
          {msg && (
            <div className="text-sm text-green-400">{msg}</div>
          )}
          <button
            onClick={handleAcquireRoleClick}
            disabled={ hasRoleAlready || form?.roles?.includes(String(role).toUpperCase())}
            className="bg-teal-600 hover:bg-teal-800 text-xs px-2 py-1 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasRoleAlready
                ? `✅ ${role} Role Already Acquired`
                : acquiringRole
                ? "⏳ Acquiring Role..."
                : role
                  ? `🚀 Acquire New Role as ${String(role).toUpperCase()}`
                  : "🚀 Acquire New Role"
              }
          </button>
        </div>
      )}

      {isNew &&  (
        <>
        <div className="bg-gray-800 p-5 rounded-xl space-y-3">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <label className="text-xs lg:text-sm text-gray-300 font-medium">
                Preferred Active Role*
              </label>

              <button
                type="button"
                onClick={() => setShowRoleHelp(!showRoleHelp)}
                className="text-yellow-300 hover:text-yellow-100 text-sm w-6"
                title="Role Selection Help"
              >
                ❓
              </button>
            </div>
            <select
              value={form.preferredActiveRole || ""}
              onChange={(e) => selectRole(e.target.value)}
              disabled={isReadOnly}
              className="w-full text-xs lg:text-sm bg-gray-700 border border-gray-600 text-white p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {showRoleHelp && (
            <div className="bg-cyan-900/20 border border-cyan-700 rounded-lg p-3">
              <div className="text-cyan-200 font-semibold text-xs mb-2">
                🎯 Choose the role that best describes how you want to engage with PurpleDoor
              </div>

              <ul className="space-y-2">
                <li className="text-xs text-gray-100">
                  🎓 <span className="font-semibold text-yellow-200">Student</span> —
                  Join for your own / children <span className="text-cyan-200">learning, growth, and transformation journey</span>.
                </li>

                <li className="text-xs text-gray-100">
                  🌍 <span className="font-semibold text-yellow-200">Director / Coordinator</span> —
                  Build and lead PurpleDoor's <span className="text-cyan-200">business, community, or partner ecosystem</span> in your city, region, or country.
                </li>

                <li className="text-xs text-gray-100">
                  👨‍👩‍👧 <span className="font-semibold text-yellow-200">Parent</span> —
                  Gain <span className="text-cyan-200">insights, guidance, and developmental support</span> for your children and family.
                </li>

                <li className="text-xs text-gray-100">
                  🧙 <span className="font-semibold text-yellow-200">Mentor</span> —
                  Ideal for <span className="text-cyan-200">teachers, trainers, counsellors, coaches, HR professionals, managers, and leaders</span> who wish to guide others.
                </li>

                <li className="text-xs text-gray-100">
                  📣 <span className="font-semibold text-yellow-200">Influencer</span> —
                  Help grow the PurpleDoor community, create awareness, and earn <span className="text-cyan-200">recognition and rewards</span>.
                </li>

                <li className="text-xs text-gray-100">
                  🏢 <span className="font-semibold text-yellow-200">Institution</span> —
                  Represent an <span className="text-cyan-200">MSME, company, school, college, university, NGO, or government body within PurpleDoor Ecosystem</span>.
                </li>
              </ul>

              <div className="mt-3 border-t border-cyan-800 pt-2 text-center">
                <span className="text-yellow-200 text-xs font-medium">
                  💡 Multiple roles can be acquired over time — start with one and expand your journey as you grow.
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-800 p-5 rounded-xl space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-cyan-500 text-sm lg:text-lg font-semibold">
              Referral Information (Compulsory)
            </h3>

            <button
              type="button"
              onClick={() => setShowReferralHelp(!showReferralHelp)}
              className="text-yellow-300 hover:text-yellow-100 text-sm w-6"
              title="Referral Information Help"
            >
              ❓
            </button>
          </div>
          {showReferralHelp && (
            <div className="mt-2 mb-3 bg-cyan-900/20 border border-cyan-700 rounded-lg p-3 text-xs text-gray-200">
              <ul className="space-y-1">
                <li className="text-xs text-gray-100">
                  <strong>Referrer Code:</strong> Unique 14-character Think5Whys ID of the person who introduced you.
                </li>

                <li className="text-xs text-gray-100">
                  <strong>Referrer Name:</strong> Full name of the person who referred or guided you.
                </li>

                <li className="text-xs text-gray-100">
                  <strong>Referrer Mobile:</strong> Your contact number.
                </li>

                <li className="text-xs text-gray-100">
                  <strong>Email ID:</strong> Your active email address for onboarding, communication and account recovery.
                </li>
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-xs text-gray-300">Referrer Code *</label>
              <input
                required
                maxLength={14}
                placeholder="Enter 14 character Think5Whys Code"
                className="w-full bg-gray-700 p-1 rounded-md text-xs"
                value={form.referredByCode}
                onChange={(e) => !isReadOnly && setForm({ ...form, referredByCode: e.target.value.toUpperCase() })}
                disabled={isReadOnly}
              />
              {form.referredByCode && form.referredByCode.length !== 14 && (
                <p className="text-red-400 text-xs mt-1">
                  Referred By / Referrer Code must be exactly 14 characters
                </p>
              )}
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-gray-300">Referrer Name *</label>
              <input
                required
                className="w-full bg-gray-700 p-1 rounded-md text-xs"
                value={form.referredByName}
                onChange={(e) => !isReadOnly && setForm({ ...form, referredByName: e.target.value })}
                disabled={isReadOnly}
              />
              {form.referredByName && form.referredByName.trim().length === 0 && (
                <p className="text-red-400 text-xs mt-1">
                  Referrer Name is required
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-xs text-gray-300">My Mobile# *</label>
              <input
                required
                type="tel"
                maxLength={10}
                className="w-full bg-gray-700 p-1 rounded-md text-xs"
                value={form.mobileNumber}
                onChange={(e) => setForm({ ...form, mobileNumber: e.target.value.replace(/\D/g, "") })}
              />

              {form.mobileNumber && form.mobileNumber.length !== 10 && (
                <p className="text-red-400 text-xs mt-1">
                  Mobile Number must be 10 digits
                </p>
              )}
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-gray-300">My Email ID *</label>
              <input
                required
                type="email"
                className="w-full bg-gray-700 p-1 rounded-md text-xs"
                style={{width: "100%"}}
                value={form.emailId}
                onChange={(e) => setForm({ ...form, emailId: e.target.value })}
              />

              {!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailId || "") && (
                <p className="text-red-400 text-xs mt-1">
                  Enter valid email address
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2">
          {msg && (
            <div className="text-xs lg:text-sm text-yellow-300">
              {msg}
            </div>
          )}
          <button
            onClick={handleCreate}
            disabled={isOnboarded}
            className="bg-teal-600 hover:bg-teal-800 px-2 py-1 rounded-md font-medium disabled:opacity-80"
          >
            Create Onboarding
          </button>
        </div>
        </>
      )}
    </div>
  );
};

export default PDOnboarding;